import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Close as CloseIcon,
  SwapHoriz as DevInternaIcon,
  Inventory2 as ProductoBIcon,
} from '@mui/icons-material';
import { ParteProduccionService } from '../services/ParteProduccionService';
import type {
  PresentacionTipoB,
  ProductoTipoBGrid,
  ProductoTipoBCelda,
  ProductoTerminadoTipoB,
} from '../types';

// Interface para devoluciones internas desde productos terminados A
export interface DevolucionInternaInfo {
  idGuiaProduccionDetalle: number;
  cantidadDevolucionInterna: number;
  pesoEmpaqueInterior: number;
}

interface ProductoTipoBSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelectProducts: (selectedProducts: ProductoTerminadoTipoB[]) => void;
  parteId?: number;
  devolucionesInternas?: DevolucionInternaInfo[];
}

interface CeldaConCantidad extends ProductoTipoBCelda {
  cantidadSeleccionada: number;
  cantidadMaxima: number;
}

// Tipo de tab: 0 = Dev. Interna, 1 = Producto B
type TabType = 0 | 1;

const ProductoTipoBSelectionModal: React.FC<ProductoTipoBSelectionModalProps> = ({
  open,
  onClose,
  onSelectProducts,
  parteId,
  devolucionesInternas = [],
}) => {
  const [activeTab, setActiveTab] = useState<TabType>(0);
  const [presentaciones, setPresentaciones] = useState<PresentacionTipoB[]>([]);
  const [selectedPresentacion, setSelectedPresentacion] = useState<number | null>(null);
  const [grid, setGrid] = useState<ProductoTipoBGrid | null>(null);
  const [celdasConCantidad, setCeldasConCantidad] = useState<CeldaConCantidad[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Totales separados por tipo y presentación
  const [totalesDevIntPorPresentacion, setTotalesDevIntPorPresentacion] = useState<Map<number, number>>(new Map());
  const [totalesTipoBPorPresentacion, setTotalesTipoBPorPresentacion] = useState<Map<number, number>>(new Map());

  // Guardar selecciones al cambiar de tab o presentación: key = `${tab}_${presentacionId}`
  const savedSelectionsRef = useRef<Map<string, CeldaConCantidad[]>>(new Map());

  const getSelectionKey = (tab: TabType, presentacionId: number) => `${tab}_${presentacionId}`;

  // Guardar selección actual antes de cambiar
  const saveCurrentSelection = useCallback(() => {
    if (selectedPresentacion != null && celdasConCantidad.length > 0) {
      const key = getSelectionKey(activeTab, selectedPresentacion);
      savedSelectionsRef.current.set(key, [...celdasConCantidad]);
    }
  }, [activeTab, selectedPresentacion, celdasConCantidad]);

  // Calcular cantidadMaxima para una celda según el filtro de tab
  const computeCeldaMaxima = useCallback((celda: ProductoTipoBCelda, tabFilter: TabType): number => {
    let cantidadMaximaTotal = 0;

    celda.detalles.forEach(detalle => {
      if (tabFilter === 1) {
        // Tab Producto B: solo contar Type B
        if (detalle.esTipoB) {
          cantidadMaximaTotal += detalle.cantidadDisponible;
        }
      } else {
        // Tab Dev. Interna: solo contar devoluciones internas
        if (detalle.esDevolucionInterna && !detalle.esTipoB) {
          const devolucion = devolucionesInternas.find(
            d => d.idGuiaProduccionDetalle === detalle.idGuiaProduccionDetalle
          );
          if (devolucion) {
            // Devolución del formulario actual
            cantidadMaximaTotal += Math.min(
              devolucion.cantidadDevolucionInterna,
              detalle.cantidadDisponible
            );
          } else {
            // Devolución de OTROS partes (ya guardada en BD)
            cantidadMaximaTotal += detalle.cantidadDisponible;
          }
        }
      }
    });

    return Math.round(cantidadMaximaTotal * 100) / 100;
  }, [devolucionesInternas]);

  const loadPresentaciones = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await ParteProduccionService.getPresentacionesTipoB(parteId);
      setPresentaciones(result);

      // Calcular totales separados por tipo para cada presentación
      const devIntMap = new Map<number, number>();
      const tipoBMap = new Map<number, number>();

      await Promise.all(result.map(async (presentacion) => {
        try {
          const gridData = await ParteProduccionService.getProductosTipoBByPresentacion(
            presentacion.idPresentacion,
            parteId
          );

          let totalDevInt = 0;
          let totalTipoB = 0;

          gridData.celdas.forEach(celda => {
            celda.detalles.forEach(detalle => {
              if (detalle.esTipoB) {
                totalTipoB += detalle.cantidadDisponible * celda.pesoEmpaqueInterior;
              } else if (detalle.esDevolucionInterna) {
                const devolucion = devolucionesInternas.find(
                  d => d.idGuiaProduccionDetalle === detalle.idGuiaProduccionDetalle
                );
                if (devolucion) {
                  const cantMax = Math.min(devolucion.cantidadDevolucionInterna, detalle.cantidadDisponible);
                  totalDevInt += cantMax * celda.pesoEmpaqueInterior;
                } else {
                  totalDevInt += detalle.cantidadDisponible * celda.pesoEmpaqueInterior;
                }
              }
            });
          });

          devIntMap.set(presentacion.idPresentacion, Math.round(totalDevInt * 100) / 100);
          tipoBMap.set(presentacion.idPresentacion, Math.round(totalTipoB * 100) / 100);
        } catch (err) {
          console.error(`Error loading products for presentacion ${presentacion.idPresentacion}:`, err);
        }
      }));

      setTotalesDevIntPorPresentacion(devIntMap);
      setTotalesTipoBPorPresentacion(tipoBMap);
    } catch (err) {
      setError('Error al cargar las presentaciones');
      console.error('Error loading presentaciones:', err);
    } finally {
      setLoading(false);
    }
  }, [parteId, devolucionesInternas]);

  const loadProductsByPresentacion = useCallback(async (idPresentacion: number, tab: TabType) => {
    // Verificar si ya tenemos selecciones guardadas
    const savedKey = getSelectionKey(tab, idPresentacion);
    const saved = savedSelectionsRef.current.get(savedKey);
    if (saved) {
      // Restaurar selecciones guardadas
      setCeldasConCantidad(saved);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await ParteProduccionService.getProductosTipoBByPresentacion(idPresentacion, parteId);
      setGrid(result);

      const celdasInit = result.celdas.map(celda => {
        const cantidadMaxima = computeCeldaMaxima(celda, tab);
        return {
          ...celda,
          cantidadMaxima,
          cantidadSeleccionada: cantidadMaxima,
        };
      }).filter(celda => celda.cantidadMaxima > 0);

      setCeldasConCantidad(celdasInit);
    } catch (err) {
      setError('Error al cargar los productos');
      console.error('Error loading products:', err);
    } finally {
      setLoading(false);
    }
  }, [parteId, computeCeldaMaxima]);

  const handleTabChange = (_: React.SyntheticEvent, newTab: TabType) => {
    saveCurrentSelection();
    setActiveTab(newTab);
    setSelectedPresentacion(null);
    setGrid(null);
    setCeldasConCantidad([]);
  };

  const handlePresentacionSelect = (idPresentacion: number) => {
    saveCurrentSelection();
    setSelectedPresentacion(idPresentacion);
  };

  const handleCantidadChange = (calibre: string, empaque: string, newValue: string) => {
    const isSaldoColumn = empaque.toLowerCase().includes('saldo');

    if (newValue === '' || newValue === '-') {
      setCeldasConCantidad(prev =>
        prev.map(celda => {
          if (celda.calibre === calibre && celda.empaque === empaque) {
            return { ...celda, cantidadSeleccionada: 0 };
          }
          return celda;
        })
      );
      return;
    }

    const cantidad = isSaldoColumn ? parseFloat(newValue) : parseInt(newValue);
    if (isNaN(cantidad)) return;

    setCeldasConCantidad(prev =>
      prev.map(celda => {
        if (celda.calibre === calibre && celda.empaque === empaque) {
          return {
            ...celda,
            cantidadSeleccionada: Math.min(Math.max(0, cantidad), celda.cantidadMaxima),
          };
        }
        return celda;
      })
    );
  };

  const handleConfirmSelection = () => {
    // Guardar selección actual
    saveCurrentSelection();

    const selectedProducts: ProductoTerminadoTipoB[] = [];

    // Helper: filtrar detalles según el tipo de tab
    const filterDetallesByTab = (detalles: typeof celdasConCantidad[0]['detalles'], tabKey: string) => {
      const tabType = parseInt(tabKey.split('_')[0]);
      if (tabType === 1) {
        // Producto B tab: solo detalles Type B
        return detalles.filter(d => d.esTipoB);
      } else {
        // Dev Interna tab: solo detalles Dev Interna (no Type B)
        return detalles.filter(d => d.esDevolucionInterna && !d.esTipoB);
      }
    };

    // Recopilar selecciones de TODAS las tabs y presentaciones
    savedSelectionsRef.current.forEach((celdas, key) => {
      // Obtener la descripción de presentación del key
      const presentacionId = parseInt(key.split('_')[1]);
      const pres = presentaciones.find(p => p.idPresentacion === presentacionId);
      const presDescripcion = pres?.descripcion || grid?.presentacionDescripcion || 'Sin Presentación';

      celdas.forEach(celda => {
        if (celda.cantidadSeleccionada > 0 && celda.detalles.length > 0) {
          let remainingQty = celda.cantidadSeleccionada;
          const filteredDetalles = filterDetallesByTab(celda.detalles, key);

          for (const detalle of filteredDetalles) {
            if (remainingQty <= 0) break;

            const qtyFromThisDetalle = Math.min(remainingQty, detalle.cantidadDisponible);
            if (qtyFromThisDetalle > 0) {
              const producto = `${detalle.descripcion} - ${celda.empaque}`;
              selectedProducts.push({
                id: detalle.idGuiaProduccionDetalle,
                uid: `${detalle.idGuiaProduccionDetalle}_${celda.empaque}_${Date.now()}_${Math.random()}`,
                idGuiaProduccion: detalle.idGuiaProduccion,
                codigo: detalle.idArticulo,
                numeroDerc: detalle.numeroDerc,
                duenio: detalle.duenio,
                encargado: detalle.encargado,
                fechaExtraccion: detalle.fechaExtraccion,
                producto,
                especie: presDescripcion,
                cantidad: qtyFromThisDetalle,
                peso: celda.pesoEmpaqueInterior,
                total: qtyFromThisDetalle * celda.pesoEmpaqueInterior,
              });
              remainingQty -= qtyFromThisDetalle;
            }
          }
        }
      });
    });

    // También incluir celdasConCantidad actual si no están guardadas
    const currentKey = selectedPresentacion != null ? getSelectionKey(activeTab, selectedPresentacion) : '';
    celdasConCantidad.forEach(celda => {
      if (celda.cantidadSeleccionada > 0 && celda.detalles.length > 0) {
        // Verificar que no esté ya incluida (por uid)
        const alreadyIncluded = selectedProducts.some(sp =>
          celda.detalles.some(d => d.idGuiaProduccionDetalle === sp.id && sp.producto?.includes(celda.empaque))
        );
        if (alreadyIncluded) return;

        let remainingQty = celda.cantidadSeleccionada;
        const filteredDetalles = currentKey ? filterDetallesByTab(celda.detalles, currentKey) : celda.detalles.filter(d => activeTab === 1 ? d.esTipoB : (d.esDevolucionInterna && !d.esTipoB));

        const pres = presentaciones.find(p => p.idPresentacion === selectedPresentacion);
        const presDescripcion = pres?.descripcion || grid?.presentacionDescripcion || 'Sin Presentación';

        for (const detalle of filteredDetalles) {
          if (remainingQty <= 0) break;
          const qtyFromThisDetalle = Math.min(remainingQty, detalle.cantidadDisponible);
          if (qtyFromThisDetalle > 0) {
            const producto = `${detalle.descripcion} - ${celda.empaque}`;
            selectedProducts.push({
              id: detalle.idGuiaProduccionDetalle,
              uid: `${detalle.idGuiaProduccionDetalle}_${celda.empaque}_${Date.now()}_${Math.random()}`,
              idGuiaProduccion: detalle.idGuiaProduccion,
              codigo: detalle.idArticulo,
              numeroDerc: detalle.numeroDerc,
              duenio: detalle.duenio,
              encargado: detalle.encargado,
              fechaExtraccion: detalle.fechaExtraccion,
              producto,
              especie: presDescripcion,
              cantidad: qtyFromThisDetalle,
              peso: celda.pesoEmpaqueInterior,
              total: qtyFromThisDetalle * celda.pesoEmpaqueInterior,
            });
            remainingQty -= qtyFromThisDetalle;
          }
        }
      }
    });

    onSelectProducts(selectedProducts);
    handleClose();
  };

  const handleClose = () => {
    setActiveTab(0);
    setSelectedPresentacion(null);
    setGrid(null);
    setCeldasConCantidad([]);
    setError(null);
    savedSelectionsRef.current.clear();
    onClose();
  };

  // Totales solo de la vista actual (celdasConCantidad)
  const getTotalSelectedWeight = () => {
    return celdasConCantidad.reduce((total, celda) => {
      return total + celda.cantidadSeleccionada * celda.pesoEmpaqueInterior;
    }, 0);
  };

  const getTotalSelectedCount = () => {
    return celdasConCantidad.reduce((total, celda) => total + celda.cantidadSeleccionada, 0);
  };

  // Totales globales (todas las tabs y presentaciones)
  const getGlobalTotals = useCallback(() => {
    let totalCount = 0;
    let totalWeight = 0;

    savedSelectionsRef.current.forEach((celdas) => {
      celdas.forEach(celda => {
        totalCount += celda.cantidadSeleccionada;
        totalWeight += celda.cantidadSeleccionada * celda.pesoEmpaqueInterior;
      });
    });

    // Agregar la selección actual si no está guardada
    const currentKey = selectedPresentacion != null
      ? getSelectionKey(activeTab, selectedPresentacion)
      : null;
    if (currentKey && !savedSelectionsRef.current.has(currentKey)) {
      celdasConCantidad.forEach(celda => {
        totalCount += celda.cantidadSeleccionada;
        totalWeight += celda.cantidadSeleccionada * celda.pesoEmpaqueInterior;
      });
    }

    return { totalCount: Math.round(totalCount * 100) / 100, totalWeight: Math.round(totalWeight * 100) / 100 };
  }, [celdasConCantidad, activeTab, selectedPresentacion]);

  const getTotalsByEmpaque = () => {
    const totals: Record<string, number> = {};
    if (!grid) return totals;

    grid.empaques.forEach(empaque => {
      totals[empaque] = 0;
    });

    celdasConCantidad.forEach(celda => {
      if (celda.cantidadSeleccionada > 0) {
        const pesoTotal = celda.cantidadSeleccionada * celda.pesoEmpaqueInterior;
        totals[celda.empaque] = (totals[celda.empaque] || 0) + pesoTotal;
      }
    });

    return totals;
  };

  const getTotalsByCalibre = () => {
    const totals: Record<string, number> = {};
    if (!grid) return totals;

    grid.calibres.forEach(calibre => {
      totals[calibre] = 0;
    });

    celdasConCantidad.forEach(celda => {
      if (celda.cantidadSeleccionada > 0) {
        const pesoTotal = celda.cantidadSeleccionada * celda.pesoEmpaqueInterior;
        totals[celda.calibre] = (totals[celda.calibre] || 0) + pesoTotal;
      }
    });

    return totals;
  };

  const getCelda = (calibre: string, empaque: string): CeldaConCantidad | undefined => {
    return celdasConCantidad.find(c => c.calibre === calibre && c.empaque === empaque);
  };

  // Presentaciones filtradas según el tab activo
  const filteredPresentaciones = presentaciones.filter(p => {
    if (activeTab === 0) {
      const total = totalesDevIntPorPresentacion.get(p.idPresentacion);
      return total !== undefined && total > 0;
    } else {
      const total = totalesTipoBPorPresentacion.get(p.idPresentacion);
      return total !== undefined && total > 0;
    }
  });

  // Calibres filtrados (solo los que tienen celdas con cantidadMaxima > 0)
  const filteredCalibres = grid?.calibres.filter(calibre =>
    celdasConCantidad.some(c => c.calibre === calibre && c.cantidadMaxima > 0)
  ) || [];

  const filteredEmpaques = grid?.empaques.filter(empaque =>
    celdasConCantidad.some(c => c.empaque === empaque && c.cantidadMaxima > 0)
  ) || [];

  // Load presentaciones when modal opens
  useEffect(() => {
    if (open) {
      loadPresentaciones();
    }
  }, [open, loadPresentaciones]);

  // Load products when presentacion is selected
  useEffect(() => {
    if (selectedPresentacion) {
      loadProductsByPresentacion(selectedPresentacion, activeTab);
    }
  }, [selectedPresentacion, loadProductsByPresentacion, activeTab]);

  // Determinar si hay datos para cada tab
  const hasDevIntData = [...totalesDevIntPorPresentacion.values()].some(v => v > 0);
  const hasTipoBData = [...totalesTipoBPorPresentacion.values()].some(v => v > 0);

  const globalTotals = getGlobalTotals();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          height: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="h6" component="div" fontWeight={600}>
            Seleccionar Productos B y Devolución Interna
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Productos tipo B y devoluciones internas disponibles
          </Typography>
        </Box>
        <Button
          onClick={handleClose}
          size="small"
          color="inherit"
          sx={{ minWidth: 'auto', p: 0.5 }}
        >
          <CloseIcon fontSize="small" />
        </Button>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ minHeight: 42 }}
        >
          <Tab
            icon={<DevInternaIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Dev. Interna
                {hasDevIntData && (
                  <Chip label={filteredPresentaciones.length} size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                )}
              </Box>
            }
            sx={{ minHeight: 42, textTransform: 'none', fontWeight: 600 }}
          />
          <Tab
            icon={<ProductoBIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Producto B
                {hasTipoBData && (
                  <Chip label={presentaciones.filter(p => (totalesTipoBPorPresentacion.get(p.idPresentacion) ?? 0) > 0).length} size="small" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
                )}
              </Box>
            }
            sx={{ minHeight: 42, textTransform: 'none', fontWeight: 600 }}
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 2, display: 'flex', gap: 2, overflow: 'hidden' }}>
        {/* Left Panel - Presentaciones */}
        <Paper sx={{ width: 300, maxHeight: '100%', overflow: 'auto', flexShrink: 0 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Presentaciones {activeTab === 0 ? 'Dev. Interna' : 'Producto B'}
            </Typography>
          </Box>

          {loading && !selectedPresentacion && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={24} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && filteredPresentaciones.length === 0 && (
            <Box p={3}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                No hay {activeTab === 0 ? 'devoluciones internas' : 'productos tipo B'} disponibles
              </Typography>
            </Box>
          )}

          <List sx={{ p: 0 }}>
            {filteredPresentaciones.map((presentacion) => {
              const isSelected = selectedPresentacion === presentacion.idPresentacion;
              const totalToShow = activeTab === 0
                ? (totalesDevIntPorPresentacion.get(presentacion.idPresentacion) ?? 0)
                : (totalesTipoBPorPresentacion.get(presentacion.idPresentacion) ?? 0);

              return (
                <ListItem key={presentacion.idPresentacion} sx={{ p: 0 }}>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => handlePresentacionSelect(presentacion.idPresentacion)}
                    sx={{
                      borderLeft: isSelected ? 3 : 0,
                      borderColor: activeTab === 0 ? 'warning.main' : 'primary.main',
                    }}
                  >
                    <ListItemText
                      primary={presentacion.descripcion}
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <Chip
                            label={presentacion.esFresco ? 'Fresco' : 'Procesado'}
                            color={presentacion.esFresco ? 'success' : 'info'}
                            size="small"
                          />
                          <Chip
                            label={`${totalToShow.toFixed(2)} kg`}
                            variant="outlined"
                            size="small"
                            color={activeTab === 0 ? 'warning' : 'primary'}
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Paper>

        {/* Right Panel - Products Grid */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {!selectedPresentacion && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              flexDirection="column"
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Seleccione una presentación
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Elija una presentación de la lista para ver los productos disponibles
              </Typography>
            </Box>
          )}

          {selectedPresentacion && loading && (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
            </Box>
          )}

          {selectedPresentacion && !loading && filteredCalibres.length > 0 && filteredEmpaques.length > 0 && (
            <Box>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {activeTab === 0 ? 'Dev. Interna' : 'Producto B'} - {grid?.presentacionDescripcion}
                </Typography>
                {getTotalSelectedCount() > 0 && (
                  <Chip
                    label={`${getTotalSelectedCount()} unidades - ${getTotalSelectedWeight().toFixed(2)} kg`}
                    color={activeTab === 0 ? 'warning' : 'primary'}
                    size="small"
                  />
                )}
              </Box>

              <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Calibre</TableCell>
                      {filteredEmpaques.map((empaque) => (
                        <TableCell key={empaque} align="center" sx={{ fontWeight: 600, minWidth: 150 }}>
                          {empaque}
                        </TableCell>
                      ))}
                      <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'warning.light', minWidth: 120 }}>
                        Total por Calibre (kg)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCalibres.map((calibre) => {
                      const totalsByCalibre = getTotalsByCalibre();
                      const totalCalibre = totalsByCalibre[calibre] || 0;

                      return (
                        <TableRow key={calibre} hover>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.50' }}>
                            {calibre}
                          </TableCell>
                          {filteredEmpaques.map((empaque) => {
                            const celda = getCelda(calibre, empaque);
                            const hasProducts = celda && celda.cantidadMaxima > 0;
                            const isSaldoColumn = empaque.toLowerCase().includes('saldo');

                            return (
                              <TableCell key={empaque} align="center">
                                {hasProducts ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                    <TextField
                                      size="small"
                                      value={celda.cantidadSeleccionada === 0 ? '' : celda.cantidadSeleccionada}
                                      onChange={(e) => handleCantidadChange(calibre, empaque, e.target.value)}
                                      onFocus={(e) => e.target.select()}
                                      placeholder="0"
                                      sx={{ width: 80 }}
                                      inputProps={{
                                        min: 0,
                                        max: celda.cantidadMaxima,
                                        type: 'number',
                                        step: isSaldoColumn ? 0.01 : 1,
                                        style: { textAlign: 'center' },
                                      }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                      Máx: {celda.cantidadMaxima}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.disabled">
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                            );
                          })}
                          <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'warning.light' }}>
                            {totalCalibre.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Fila de totales de peso por empaque */}
                    <TableRow sx={{ bgcolor: activeTab === 0 ? 'warning.light' : 'primary.light', '& td': { borderTop: 2, borderColor: activeTab === 0 ? 'warning.main' : 'primary.main' } }}>
                      <TableCell sx={{ fontWeight: 700 }}>
                        PESO TOTAL (kg)
                      </TableCell>
                      {filteredEmpaques.map((empaque) => {
                        const totalsByEmpaque = getTotalsByEmpaque();
                        const pesoTotal = totalsByEmpaque[empaque] || 0;

                        return (
                          <TableCell key={empaque} align="center" sx={{ fontWeight: 700 }}>
                            {pesoTotal.toFixed(2)}
                          </TableCell>
                        );
                      })}
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: 700,
                          bgcolor: 'secondary.main',
                          color: 'secondary.contrastText',
                          fontSize: '1.1rem',
                          border: 3,
                          borderColor: 'secondary.dark'
                        }}
                      >
                        {getTotalSelectedWeight().toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {selectedPresentacion && !loading && (filteredCalibres.length === 0 || filteredEmpaques.length === 0) && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              flexDirection="column"
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No hay productos disponibles
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No se encontraron {activeTab === 0 ? 'devoluciones internas' : 'productos tipo B'} para esta presentación
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmSelection}
          variant="contained"
          color="primary"
          disabled={globalTotals.totalCount === 0}
          sx={{ fontWeight: 600 }}
        >
          Confirmar Selección ({globalTotals.totalCount} unidades - {globalTotals.totalWeight.toFixed(2)} kg)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductoTipoBSelectionModal;
